import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { MatInput } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContactsService } from '@nimbus-assistant/services/contacts.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { isIncludeOnlyValidPhoneNumberSymbols, phoneNumberError } from '@nimbus-library/features/shared/components/material/core';
import { noAutocompleteRecords } from '@nimbus-library/features/shared/components/material/core/no-records';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import {
  UserAutoCompleteCallOptionType,
  UserAutoCompleteValueType,
} from '@nimbus-library/features/shared/services/user-search/models/user-auto-complete-value-type';
import { userAutocompletePhoneResultFromSearchValue } from '@nimbus-library/features/shared/components/material/input-user-autocomplete/utils';
import { TaskTransferService } from '../../../services/task-transfer.service';
import { ServicesService } from '../../../services/services.service';
import { IAssistantUserAutoCompleteCallOption, IAssistantUserAutocompleteOption } from '../../../interfaces/assistant-user-autocomplete';
import { AuthService } from '../../../services/auth.service';
import { TaskOutboundService } from '../../../services/task-outbound.service';
import { IAssistantUserAutoCompleteValueModel } from '../../../interfaces/assistant-user-autocomplete-value-model';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-nimbus-input-assistant-autocomplete',
  templateUrl: './input-assistant-user-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputAssistantUserAutocompleteComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => InputAssistantUserAutocompleteComponent),
    },
  ],
})
export class InputAssistantUserAutocompleteComponent
  extends IInputComponent<IAssistantUserAutoCompleteValueModel | string>
  implements OnInit, OnDestroy, Validator
{
  private static readonly DEBOUNCE_MS = 350;

  @ViewChild(MatInput, { static: true })
  public input: MatInput;
  @ViewChild('auto', { static: true })
  public autocomplete: ElementRef<MatAutocomplete>;
  @Input()
  public isPhoneNumberDisabled = false;
  @Output() public selectionChange = new EventEmitter<IIdName | null>();

  private isChangedFromSelect: boolean = false;

  public icons = ICONS;
  public TranslationKeys = TranslationKeys;
  public filteredOptions: IAssistantUserAutocompleteOption[] = [];
  public tenantId = '';
  public UserAutoCompleteCallOptionType = UserAutoCompleteCallOptionType;
  public control: UntypedFormControl = new UntypedFormControl();
  public readonly nimbusServiceImagePath = 'assets/images/nimbus-team.png';

  public constructor(
    private translateService: TranslateService,
    private changeDetectionRef: ChangeDetectorRef,
    private authService: AuthService,
    public outboundService: TaskOutboundService,
    public contactsService: ContactsService,
    public transferService: TaskTransferService,
    private readonly servicesService: ServicesService,
    public injector: Injector,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.tenantId = this.authService.getTenantId();

    this.servicesService.serviceApplicationMappings$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateTransferBlockedReasons(this.filteredOptions);
      this.changeDetectionRef.markForCheck();
    });

    this.control.valueChanges
      .pipe(
        debounceTime(InputAssistantUserAutocompleteComponent.DEBOUNCE_MS),
        untilDestroyed(this),
        switchMap((searchValue: string) => {
          if (searchValue) {
            searchValue = searchValue.trim();
          }
          const minSearchLength = 2;
          if (!this.isChangedFromSelect && this.control.touched) {
            this.value = null;
            this.filteredOptions = [];
          }
          this.isChangedFromSelect = false;

          const phoneResult: IAssistantUserAutocompleteOption[] = userAutocompletePhoneResultFromSearchValue(searchValue);

          let usersResults$: Observable<IAssistantUserAutocompleteOption[]> = of([]);

          if (searchValue && searchValue.length > minSearchLength) {
            const includeServices = !!this.transferService.callTransfer$.value;
            usersResults$ = this.contactsService.search(searchValue, this.tenantId, includeServices);
          }

          return usersResults$.pipe(
            map((x: IAssistantUserAutocompleteOption[]) => {
              let result = x.filter((user) => user.id !== this.authService.profile$.value.id);

              if (!this.isPhoneNumberDisabled) {
                result = phoneResult.concat(result);
              }

              if (!result.length && searchValue) {
                if (!this.isPhoneNumberDisabled) {
                  if (isIncludeOnlyValidPhoneNumberSymbols(searchValue)) {
                    this.value = 'not_valid_phone_number';
                  } else {
                    this.value = null;
                    this.propagateChange(null);
                    result = result.concat(noAutocompleteRecords(this.translateService.instant(TranslationKeys.CallAsNoContactsFound)));
                  }
                } else {
                  this.value = null;
                  this.propagateChange(null);
                  result = result.concat(noAutocompleteRecords(this.translateService.instant(TranslationKeys.CallAsNoContactsFound)));
                }
              }
              return result;
            }),
          );
        }),
        catchError((error, object) => object),
      )
      .subscribe((result) => {
        this.setOptions(result);
        this.changeDetectionRef.markForCheck();
      });
  }

  public get isInvalidAutocomplete(): boolean {
    return this.ngControl && (this.ngControl.dirty || this.ngControl.touched) && this.ngControl.invalid;
  }

  public get isInvalidPhoneNumber(): boolean {
    return JSON.stringify(this.ngControl.errors) === JSON.stringify(phoneNumberError());
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event) {
      this.setValue(event.option.value);
      this.selectionChange.emit({
        id: (this.value as IAssistantUserAutoCompleteValueModel).value,
        name: event.option.value.name,
      } as IIdName);
    }

    this.control.updateValueAndValidity();
  }

  public setOptions(options: IAssistantUserAutocompleteOption[]): void {
    this.updateTransferBlockedReasons(options);
    this.filteredOptions = options;
    this.input.onContainerClick();
    this.changeDetectionRef.markForCheck();
  }

  public onClearValueClick(event: Event): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !!event ? event.stopPropagation() : null;
    this.value = null;
    this.control.setValue(null);
    this.setOptions([]);
  }

  public ngOnDestroy(): void {
    this.outboundService.clickToCallTarget$.next(null);
  }

  public isCallOptionDisabled(callOption: IAssistantUserAutoCompleteCallOption): boolean {
    if (this.isTransferBlocked(callOption)) {
      return true;
    }
    return callOption.type !== UserAutoCompleteCallOptionType.upn ? this.isPhoneNumberDisabled : false;
  }

  public getCallOptionIcon(callOption: IAssistantUserAutoCompleteCallOption): Icon {
    if (callOption.type === UserAutoCompleteCallOptionType.upn && !callOption.isService) {
      return this.icons.USER_LIGHT;
    }
    return callOption.icon;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (control.value === 'not_valid_phone_number') {
      return phoneNumberError();
    }
    return null;
  }

  private updateTransferBlockedReasons(options: IAssistantUserAutocompleteOption[]): void {
    options.forEach((option) => (option.transferBlockedReason = this.getTransferBlockedReason(option)));
  }

  private getTransferBlockedReason(option: IAssistantUserAutocompleteOption): TranslationKeys | undefined {
    const blockedCallOption = option.callOptions?.find((callOption) => this.isTransferBlocked(callOption));
    return blockedCallOption ? this.transferService.getTransferToServiceBlockedReason(blockedCallOption.text) : undefined;
  }

  private isTransferBlocked(callOption: IAssistantUserAutoCompleteCallOption): boolean {
    return this.transferService.isTransferToServiceBlocked(callOption.text);
  }

  private setValue(option: IAssistantUserAutoCompleteCallOption | null): void {
    this.isChangedFromSelect = true;
    if (option) {
      this.value = {
        value: option.type === UserAutoCompleteCallOptionType.upn && !option.isService ? option.userId : option.text,
        type: option.type === UserAutoCompleteCallOptionType.upn ? UserAutoCompleteValueType.GUID : UserAutoCompleteValueType.PSTN,
        isService: option.isService,
      };

      this.control.setValue(option.text, { emitEvent: false });
    } else {
      this.value = null;
      this.control.setValue(null, { emitEvent: false });
    }
  }
}
